import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import React from "react";

import { css } from "@emotion/react";

import { CSS_MQ } from "../../styles/breakpoints";
import { HotSpotNavigation } from "./HotSpotNavigation";
import { HotspotHr } from "./elements/HotspotHr";
import { EmoHeadline, SeoHeadline } from "./elements/headlines";
import { ClickAndInfoMedia } from "./elements/ClickAndInfoMedia";
import { ProductClickAndInfoType, ProductHotSpotType } from "@swo/storefront";
import { fontColor, FONTS } from "../../styles/fonts";
import { plainTextStyles } from "../../styles/text-styles";
import {
  EXPERIMENTAL_TextWithProductNameRenderer,
  useTextWithProductNameRenderer,
} from "../../domain/swarovski-optik/products/product-name/product-name-renderer";
import { withCapitalizationClassName } from "../../domain/swarovski-optik/products/product-name/label-contains-product-name";

export const DisplayClickAndInfoContent: ReactTFC<{
  hotSpots: ProductClickAndInfoType["hotSpotsCollection"];
  activeIdx: number;
  onNavigationRequested: (dir: "back" | "forward") => void;
  translations: ProductClickAndInfoType["translations"];
}> = props => {
  return (
    <div
      css={css`
        display: grid;

        grid-template-columns: auto;
        grid-template-rows: auto;

        --v: 10px;
        ${CSS_MQ.atLeastLg} {
          --v: 40px;
        }

        --negated-v: calc(-1 * var(--v));

        @keyframes slide-in-right {
          from {
            transform: translateX(var(--v));
            opacity: 0.75;
          }

          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slide-in-left {
          from {
            transform: translateX(var(--negated-v));
            opacity: 0.75;
          }

          to {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
        }
      `}
    >
      {props.hotSpots.map((hotSpot, idx) => {
        return <DisplayClickAndInfoContentElement hotSpot={hotSpot} isActive={idx === props.activeIdx} />;
      })}

      {props.hotSpots.length > 1 && (
        <HotSpotNavigation translations={props.translations} onNavigationRequested={props.onNavigationRequested} />
      )}
    </div>
  );
};

const DisplayClickAndInfoContentElement: ReactTFC<{ hotSpot: ProductHotSpotType; isActive: boolean }> = ({
  hotSpot,
  isActive,
}) => {
  const { Element, hasAnyProductName } = useTextWithProductNameRenderer(hotSpot.emotionalHeadline);

  return (
    <div className={isActive ? "visible" : "hidden"} css={hotspotStyles}>
      <ClickAndInfoMedia media={hotSpot.media} isActive={isActive} />

      <SeoHeadline>{hotSpot.descriptiveHeadline}</SeoHeadline>
      <EmoHeadline className={withCapitalizationClassName(hasAnyProductName)}>{Element}</EmoHeadline>

      <HotspotHr />

      <p className={"slide-in-right"} css={paragraphStyles}>
        {hotSpot.text}
      </p>
    </div>
  );
};

const hotspotStyles = css`
  grid-area: 1 / 1 / span 1 / span 1;

  display: grid;
  grid-template-rows: auto auto auto auto 1fr;

  &.visible {
    visibility: visible;

    .slide-in-right {
      transform: translateX(var(--v));
      opacity: 0.75;

      animation: slide-in-right 0.3s ease-out;
      animation-fill-mode: forwards;
    }

    .slide-in-left {
      transform: translateX(var(--negated-v));
      opacity: 0.75;

      animation: slide-in-left 0.3s ease-out;
      animation-fill-mode: forwards;
    }
  }

  &.hidden {
    visibility: hidden;
  }
`;

const paragraphStyles = css`
  ${FONTS.SwarovskiFutura300};
  ${fontColor.black};

  ${plainTextStyles};

  margin-bottom: 20px;
  ${CSS_MQ.atLeastMd} {
    margin-bottom: 60px;
  }
`;
